import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Stethoscope, Plus, TrendingUp, LogOut, CreditCard, Target, BookOpen, GraduationCap, UserPlus, CheckCircle, X, LogOut as LeaveIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [difficulty, setDifficulty] = useState('intermediate');
  
  // Invite code state
  const [showInviteInput, setShowInviteInput] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [joiningClass, setJoiningClass] = useState(false);
  const [leavingClass, setLeavingClass] = useState(false);
  const [educatorName, setEducatorName] = useState(user?.educator_name || null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scenariosRes, performanceRes] = await Promise.all([
        axios.get('/patients'),
        axios.get('/analytics/performance')
      ]);
      setScenarios(scenariosRes.data);
      setPerformance(performanceRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const generatePatient = async () => {
    setGenerating(true);
    try {
      const response = await axios.post('/patients/generate', {
        difficulty_level: difficulty,
        specialty: 'general'
      });
      toast.success('New patient generated!');
      setScenarios([response.data, ...scenarios]);
      // Refresh user data to get updated generation count
      const userResponse = await axios.get('/auth/me');
      window.location.reload(); // Reload to update user state
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Free tier limit reached! Upgrade to Pro for unlimited patient generations.', { duration: 5000 });
        setTimeout(() => navigate('/subscription'), 2000);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate patient');
      }
    } finally {
      setGenerating(false);
    }
  };

  const startScenario = async (scenarioId) => {
    // Allow free testing - removed subscription check
    try {
      const response = await axios.post('/sessions/start', null, {
        params: { scenario_id: scenarioId }
      });
      navigate(`/simulation/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to start simulation');
    }
  };

  const handleJoinClass = async () => {
    if (!inviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }
    
    setJoiningClass(true);
    try {
      const response = await axios.post('/educator/accept-invite', {
        invite_code: inviteCode.trim().toUpperCase()
      });
      
      toast.success(`Successfully joined ${response.data.educator_name}'s class!`);
      setEducatorName(response.data.educator_name);
      setShowInviteInput(false);
      setInviteCode('');
      
      // Refresh user data
      window.location.reload();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to join class. Check your invite code.';
      toast.error(errorMsg);
    } finally {
      setJoiningClass(false);
    }
  };

  const handleLeaveClass = async () => {
    if (!window.confirm('Are you sure you want to leave this class? Your educator will no longer be able to track your progress.')) {
      return;
    }
    
    setLeavingClass(true);
    try {
      const response = await axios.post('/educator/leave-class');
      toast.success(response.data.message);
      setEducatorName(null);
      
      // Refresh to update user data
      window.location.reload();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to leave class.';
      toast.error(errorMsg);
    } finally {
      setLeavingClass(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{backgroundColor: '#f8fafc'}}>
      {/* Navigation */}
      <nav className="border-b border-border bg-white dark:bg-slate-900 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Stethoscope className="w-8 h-8 text-primary" />
            <span className="text-2xl font-bold text-primary font-['Manrope']">ClinicalSim AI</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm" style={{color: '#334155', fontWeight: '500'}}>Welcome, {user.full_name}</span>
            <Button data-testid="advanced-sim-button" variant="outline" onClick={() => navigate('/advanced-simulation')} className="border-2 border-purple-300 text-purple-700 hover:bg-purple-50">
              <Target className="w-4 h-4 mr-2" />
              Advanced Mode
            </Button>
            <Button data-testid="nclex-practice-button" variant="outline" onClick={() => navigate('/nclex-practice')} className="border-2 border-blue-300 text-blue-700 hover:bg-blue-50">
              <BookOpen className="w-4 h-4 mr-2" />
              NCLEX Practice
            </Button>
            <Button data-testid="analytics-nav-button" variant="outline" onClick={() => navigate('/analytics')} className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100">
              <TrendingUp className="w-4 h-4 mr-2" />
              Analytics
            </Button>
            {/* Only show Educator button if user has educator subscription */}
            {user.educator_subscription_active && (
              <Button data-testid="educator-nav-button" variant="outline" onClick={() => navigate('/educator')} className="border-2 border-amber-300 text-amber-700 hover:bg-amber-50">
                <GraduationCap className="w-4 h-4 mr-2" />
                Educator
              </Button>
            )}
            {user.subscription_status !== 'active' && (
              <Button data-testid="subscribe-nav-button" onClick={() => navigate('/subscription')} className="bg-[#0F766E] hover:bg-[#0d6960] text-white">
                <CreditCard className="w-4 h-4 mr-2" />
                Upgrade to Pro
              </Button>
            )}
            <Button data-testid="logout-button" variant="outline" onClick={onLogout} className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100">
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8" data-testid="dashboard-content">
        {/* Trial Banner - Show for trial users */}
        {user.subscription_status !== 'active' && !user.trial_expired && (
          <div className="mb-6 p-4 rounded-xl border-2 border-[#0F766E] bg-gradient-to-r from-teal-50 to-cyan-50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#0F766E] flex items-center justify-center text-white">
                  <span className="text-2xl font-bold">{user.trial_days_remaining || 7}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold font-['Manrope']" style={{color: '#0f172a'}}>
                    🎉 7-Day Free Trial
                  </h3>
                  <p className="text-sm" style={{color: '#475569'}}>
                    {user.trial_days_remaining || 7} days remaining • {user.trial_patient_used ? '0' : '1'} patient generation left
                  </p>
                  <p className="text-xs mt-1" style={{color: '#64748b'}}>
                    After trial ends, you'll be automatically subscribed at <strong>$4.99/month</strong>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs mb-2" style={{color: '#64748b'}}>
                  {user.payment_method_saved ? '✅ Payment method saved' : '⚠️ Add payment method to continue after trial'}
                </p>
                {!user.payment_method_saved && (
                  <Button 
                    onClick={() => navigate('/subscription')} 
                    className="bg-[#0F766E] hover:bg-[#0d6960] text-white"
                    size="sm"
                  >
                    Add Payment Method
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Trial Expired Banner */}
        {user.subscription_status !== 'active' && user.trial_expired && (
          <div className="mb-6 p-4 rounded-xl border-2 border-red-300 bg-red-50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold font-['Manrope'] text-red-700">
                  ⏰ Your Free Trial Has Ended
                </h3>
                <p className="text-sm text-red-600">
                  Subscribe now to continue accessing unlimited patient simulations
                </p>
              </div>
              <Button 
                onClick={() => navigate('/subscription')} 
                className="bg-[#0F766E] hover:bg-[#0d6960] text-white"
              >
                Subscribe Now - $4.99/month
              </Button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="stat-card">
            <h3 className="text-sm font-medium mb-2" style={{color: '#475569'}}>Scenarios Completed</h3>
            <p className="text-3xl font-bold text-primary font-['Manrope']">{performance?.scenarios_completed || 0}</p>
          </div>
          <div className="stat-card">
            <h3 className="text-sm font-medium mb-2" style={{color: '#475569'}}>Average Score</h3>
            <p className="text-3xl font-bold text-success font-['Manrope']">{performance?.average_score?.toFixed(1) || '0.0'}%</p>
          </div>
          <div className="stat-card">
            <h3 className="text-sm font-medium mb-2" style={{color: '#475569'}}>Trial Status</h3>
            {user.subscription_status === 'active' ? (
              <p className="text-xl font-bold text-success font-['Manrope']">Pro Active</p>
            ) : user.trial_expired ? (
              <p className="text-xl font-bold text-destructive font-['Manrope']">Trial Ended</p>
            ) : (
              <>
                <p className="text-xl font-bold font-['Manrope']" style={{color: '#0F766E'}}>
                  {user.trial_days_remaining || 7} days left
                </p>
                <p className="text-xs mt-1" style={{color: '#64748b'}}>
                  Auto-renews at $4.99/mo
                </p>
              </>
            )}
          </div>
          <div className="stat-card">
            <h3 className="text-sm font-medium mb-2" style={{color: '#475569'}}>Subscription</h3>
            <p className="text-xl font-semibold font-['Manrope'] capitalize">
              {user.subscription_status === 'active' ? (
                <span className="text-success">Pro ($4.99/mo)</span>
              ) : (
                <span style={{color: user.trial_expired ? '#ef4444' : '#0F766E'}}>
                  {user.trial_expired ? 'Inactive' : '7-Day Trial'}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Join Educator Class Section - Show if user is not already in a class and not an educator */}
        {user.role !== 'educator' && (
          <div className="medical-card p-6 mb-8 border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50" style={{backgroundColor: 'white'}} data-testid="join-class-section">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  {user.educator_id || educatorName ? (
                    <>
                      <h3 className="text-lg font-semibold font-['Manrope']" style={{color: '#0f172a'}}>
                        <CheckCircle className="w-5 h-5 inline mr-2 text-emerald-500" />
                        You're in a Class
                      </h3>
                      <p className="text-sm" style={{color: '#475569'}}>
                        Your progress is being tracked by your educator
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold font-['Manrope']" style={{color: '#0f172a'}}>
                        Join an Educator's Class
                      </h3>
                      <p className="text-sm" style={{color: '#475569'}}>
                        Have an invite code? Enter it here to join your instructor's class
                      </p>
                    </>
                  )}
                </div>
              </div>
              
              {/* Show Leave Class button if user is in a class */}
              {(user.educator_id || educatorName) && (
                <Button
                  onClick={handleLeaveClass}
                  disabled={leavingClass}
                  variant="outline"
                  className="border-2 border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="leave-class-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {leavingClass ? 'Leaving...' : 'Leave Class'}
                </Button>
              )}
              
              {/* Show Join Class input if user is NOT in a class */}
              {!user.educator_id && !educatorName && (
                <div className="flex items-center gap-3">
                  {showInviteInput ? (
                    <>
                      <div className="relative">
                        <input
                          type="text"
                          value={inviteCode}
                          onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          maxLength={8}
                          className="w-32 px-3 py-2 border-2 border-amber-300 rounded-lg text-center font-mono text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 uppercase"
                          data-testid="invite-code-input"
                          disabled={joiningClass}
                        />
                      </div>
                      <Button
                        onClick={handleJoinClass}
                        disabled={joiningClass || !inviteCode.trim()}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="join-class-btn"
                      >
                        {joiningClass ? 'Joining...' : 'Join'}
                      </Button>
                      <button
                        onClick={() => { setShowInviteInput(false); setInviteCode(''); }}
                        className="p-2 text-slate-400 hover:text-slate-600"
                        data-testid="cancel-join-btn"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </>
                  ) : (
                    <Button
                      onClick={() => setShowInviteInput(true)}
                      variant="outline"
                      className="border-2 border-amber-400 text-amber-700 hover:bg-amber-100"
                      data-testid="enter-invite-code-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Enter Invite Code
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Generate Patient Section */}
        <div className="medical-card p-6 mb-8" style={{backgroundColor: 'white'}}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold font-['Manrope'] mb-2" style={{color: '#0f172a'}}>Generate New Patient</h2>
              <p className="text-base" style={{color: '#475569'}}>
                {user.subscription_status === 'active' 
                  ? 'Create unlimited AI-powered patient scenarios'
                  : user.trial_expired 
                    ? 'Your trial has ended. Subscribe to generate patients.'
                    : 'Generate 1 patient during your 7-day free trial'
                }
              </p>
            </div>
            <div className="flex gap-4 items-center">
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger data-testid="difficulty-select" className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                data-testid="generate-patient-button" 
                onClick={generatePatient} 
                disabled={generating || (user.subscription_status !== 'active' && (user.trial_expired || user.trial_patient_used))} 
                className="bg-[#0F766E] hover:bg-[#0d6960] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                {generating ? 'Generating...' : 'Generate Patient'}
              </Button>
            </div>
          </div>
          {user.subscription_status !== 'active' && (user.trial_expired || user.trial_patient_used) && (
            <div className="mt-4 p-4 rounded-lg" style={{backgroundColor: '#fef3c7', border: '1px solid #fbbf24'}}>
              <p className="text-sm font-medium" style={{color: '#92400e'}}>
                {user.trial_expired && !user.trial_patient_used 
                  ? 'Your 7-day free trial has expired.'
                  : 'You\'ve used your free trial patient.'}
                {' '}Upgrade to Pro for unlimited patient generations!
              </p>
              <Button 
                onClick={() => navigate('/subscription')} 
                className="mt-2 bg-[#0F766E] hover:bg-[#0d6960] text-white"
                size="sm"
              >
                Upgrade to Pro - $4.99/month
              </Button>
            </div>
          )}
        </div>

        {/* Scenarios List */}
        <div>
          <h2 className="text-2xl font-semibold font-['Manrope'] mb-6" style={{color: '#0f172a'}}>Patient Library</h2>
          {scenarios.length === 0 ? (
            <div className="medical-card p-12 text-center text-muted-foreground">
              <p>No patients available. Generate your first patient to get started!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {scenarios.map((scenario) => (
                <div key={scenario.id} className="scenario-card" data-testid={`scenario-card-${scenario.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-lg font-semibold font-['Manrope'] mb-1" style={{color: '#0f172a'}}>{scenario.patient_name}</h3>
                      <p className="text-sm" style={{color: '#475569'}}>
                        {scenario.age}yo {scenario.gender} • {scenario.ethnicity}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                      scenario.difficulty_level === 'beginner' ? 'bg-green-100 text-green-700' :
                      scenario.difficulty_level === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {scenario.difficulty_level}
                    </span>
                  </div>
                  <div className="space-y-2 mb-4 text-sm">
                    <div className="p-2 rounded-md" style={{backgroundColor: '#f1f5f9'}}>
                      <p className="font-medium" style={{color: '#0F766E'}}>Chief Complaint:</p>
                      <p style={{color: '#334155'}}>{scenario.chief_complaint}</p>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <p className="font-medium" style={{color: '#64748b'}}>Diagnosis:</p>
                        <p style={{color: '#0f172a'}}>{scenario.diagnosis}</p>
                      </div>
                    </div>
                    {scenario.patient_background && (
                      <div className="pt-2 border-t border-slate-200">
                        <p className="text-xs italic" style={{color: '#64748b'}}>{scenario.patient_background}</p>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1 pt-1">
                      {scenario.medical_history?.slice(0, 3).map((condition, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs rounded-full" style={{backgroundColor: '#e2e8f0', color: '#475569'}}>
                          {condition}
                        </span>
                      ))}
                      {scenario.medical_history?.length > 3 && (
                        <span className="px-2 py-0.5 text-xs rounded-full" style={{backgroundColor: '#e2e8f0', color: '#475569'}}>
                          +{scenario.medical_history.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                  <Button 
                    data-testid={`start-scenario-button-${scenario.id}`}
                    onClick={() => startScenario(scenario.id)} 
                    className="w-full bg-[#0F766E] hover:bg-[#0d6960] text-white"
                  >
                    Start Simulation
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;